import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { signOut } from "firebase/auth";
import { toast } from "sonner";
import { auth } from "@/firebase/app";
import { trpc } from "@/trpc/client";
import { DASHBOARD_HOME } from "@/lib/routes";
import { getEntryWorkspace } from "@/lib/workspaces";
import {
  beginSignupProvisioning,
  endSignupProvisioning,
  isSignupProvisioning,
} from "@/lib/signupProvisioning";
import {
  isAccountDeletedError,
  isDomainBlockedError,
} from "@/lib/trpcErrors";
import { handleAccountDeleted } from "@/lib/authErrors";
import { LoginButtons } from "@/components/auth/LoginButtons";
import { pushAuthSuccessEvent, pushSignupStart } from "@/lib/gtm";
import { usePushOnce } from "@/lib/usePushOnce";

export const Route = createFileRoute("/_home/login")({
  beforeLoad: async ({ context }) => {
    if (context.auth.user) {
      let workspace;
      try {
        workspace = await getEntryWorkspace();
        // First-bounce guard. `onAuthStateChanged` can re-run this guard the
        // instant the popup resolves — a hair before `onFirebaseUser` raises
        // the signup barrier. If the read above slipped past an unraised
        // barrier it returns null; re-read once provisioning is in flight —
        // getEntryWorkspace awaits the (now-raised) barrier itself — before
        // treating a missing workspace as "no workspace" and bouncing to /.
        if (!workspace && isSignupProvisioning()) {
          workspace = await getEntryWorkspace();
        }
      } catch (err) {
        if (isAccountDeletedError(err)) {
          try {
            await signOut(auth);
          } catch {
            /* ignore */
          }
          throw redirect({ to: "/account-deleted" });
        }
        throw err;
      }
      if (!workspace) {
        throw redirect({ to: "/" });
      }
      throw redirect({
        to: DASHBOARD_HOME,
        params: { workspaceId: workspace.workspaceId },
      });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();

  usePushOnce(pushSignupStart, "signup_start");

  const createUserMutation = useMutation({
    ...trpc.authenticatedRouterFunction.createUser.mutationOptions(),
    onSuccess: async ({ isNewUser }) => {
      // Provisioning is done — the row + first workspace now exist. Lower the
      // barrier BEFORE navigating: navigate() awaits the destination guard,
      // which reads workspaces through the same barrier, so releasing it here
      // (rather than when this whole promise chain settles) is what keeps the
      // dashboard navigation from waiting on a barrier that waits on it.
      endSignupProvisioning();
      pushAuthSuccessEvent({ method: "google", isNewUser });
      const workspace = await getEntryWorkspace();
      if (!workspace) {
        await navigate({ to: "/" });
        return;
      }
      await navigate({
        to: DASHBOARD_HOME,
        params: { workspaceId: workspace.workspaceId },
      });
    },
    onError: async (error) => {
      // Release the barrier first: a failed signup must never wedge later reads.
      endSignupProvisioning();
      if (isAccountDeletedError(error)) {
        await handleAccountDeleted(navigate);
        return;
      }
      try {
        await signOut(auth);
      } catch {
        /* ignore */
      }
      if (isDomainBlockedError(error)) {
        const { reason, waitlisted } = error.data.domainBlocked;
        if (reason === "not_allowlisted") {
          toast.error("Signup is currently invite-only.");
        } else {
          toast.success(
            waitlisted
              ? "Looks like a personal address. We've added you to the waitlist. We'll be in touch."
              : "Looks like a personal address. We'll keep you posted.",
          );
        }
        await navigate({ to: "/" });
        return;
      }
      // `/login` is Google-only. Google FORBIDDEN rejections carry
      // internal/diagnostic wording ("Email is not verified", etc.) that must
      // not be shown to users — fall through to the generic toast below.
      // (Email/password sign-in and its user-facing allowlist messages live on
      // the dedicated `/team-login` page.)
      console.error("Login failed:", error);
      toast.error("Login failed. Please try again.");
    },
  });

  return (
    <main className="flex flex-1 items-center justify-center">
      <div className="w-full max-w-sm px-6">
        <div className="mb-8 flex justify-center">
          <h1 className="font-headline uppercase text-2xl tracking-tight text-[color:var(--gs-text)]">
            Log In
          </h1>
        </div>

        <LoginButtons
          onFirebaseUser={({ email }) => {
            // Idempotency guard. The popup clears `activeProvider` a hair
            // before `createUser.isPending` propagates back down to disable the
            // button, so a second sign-in can slip through. Bail if a signup is
            // already provisioning — a second `beginSignupProvisioning()` would
            // resolve the live barrier (releasing guards parked mid-provisioning
            // and re-bouncing the new account to `/`) and fire a duplicate
            // `createUser`. `isSignupProvisioning()` reads live module state, so
            // it never lags behind the click the way `isPending` can.
            if (isSignupProvisioning()) return;
            // Raise the barrier *before* the mutation goes out: Firebase has
            // already fired onAuthStateChanged by now, and the resulting
            // router.invalidate() re-runs this route's beforeLoad. Without
            // this, that guard reads an empty workspace list mid-signup and
            // redirects a brand-new account to the marketing home. The barrier
            // is lowered in the mutation's onSuccess/onError, not tied to its
            // promise (see signupProvisioning.ts).
            beginSignupProvisioning();
            createUserMutation.mutate({ email });
          }}
          onError={(message) => toast.error(message)}
          isPending={createUserMutation.isPending}
        />
      </div>
    </main>
  );
}
